import { SiteHeader } from '../../shared/components/SiteHeader';
import { SiteFooter } from '../../shared/components/SiteFooter';
import { PageHero } from '../../shared/components/PageHero';
import { useLang } from '../../../i18n/LanguageContext';
import { servicesDict } from '../../../i18n/dictionaries/services';
import type { Localized } from '../../../i18n/types';
import styles from './ServicesPage.module.css';
import heroImage from '../../../assets/services/hero.png';
import fintechBanner from '../../../assets/services/fintech-banner.png';
import aiBanner from '../../../assets/services/ai-banner.png';
import dxBanner from '../../../assets/services/dx-banner.png';
import ifaBanner from '../../../assets/services/ifa-banner.png';
import wealthBanner from '../../../assets/services/wealth-banner.png';
import turtleBanner from '../../../assets/services/turtle-banner.png';

type CardLayout = {
  /** Banner image per language. The banner text is baked into the image. */
  readonly image: Localized<string>;
  readonly captionHidden?: boolean;
  readonly href?: string;
};

// Structural data (images / link / caption visibility) shared across languages.
// The caption text itself lives in `servicesDict`, zipped by index below.
// TODO: 中国語バナー画像が用意され次第、各 zh を *-zh.png の import に差し替える。
const CARD_LAYOUT: readonly (readonly CardLayout[])[] = [
  [
    { image: { ja: fintechBanner, zh: fintechBanner }, captionHidden: true },
    { image: { ja: aiBanner, zh: aiBanner }, captionHidden: true },
    { image: { ja: dxBanner, zh: dxBanner }, captionHidden: true },
  ],
  [
    { image: { ja: ifaBanner, zh: ifaBanner }, captionHidden: true },
    { image: { ja: wealthBanner, zh: wealthBanner }, captionHidden: true },
    {
      image: { ja: turtleBanner, zh: turtleBanner },
      captionHidden: true,
      href: 'https://turtle-trader.jp/',
    },
  ],
];

export const ServicesPage = (): JSX.Element => {
  const { lang } = useLang();
  const t = servicesDict[lang];

  return (
    <div className={styles.page}>
      <SiteHeader variant="solid" />

      <PageHero image={heroImage} eyebrow="SERVICE" title={t.heroTitle} />

      <section className={styles.intro}>
        <h2 className={styles.introTitle}>{t.introTitle}</h2>
        <p className={styles.introText}>{t.introText}</p>
      </section>

      {t.blocks.map((block, blockIndex) => (
        <section key={block.number} className={styles.service}>
          <div className={styles.serviceInner}>
            <div className={styles.serviceHead}>
              <div>
                <p className={styles.eyebrow}>
                  <span className={styles.eyebrowText}>SERVICE</span>
                  <span className={styles.eyebrowNum}>{block.number}</span>
                </p>
                <h2 className={styles.serviceTitle}>{block.title}</h2>
                <p className={styles.serviceLead}>
                  {block.lead.map((line, index) => (
                    <span key={line}>
                      {index > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </div>
              <div className={styles.serviceBody}>
                {block.body.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </div>

            <div className={styles.cards}>
              {block.cards.map((card, cardIndex) => {
                const layout = CARD_LAYOUT[blockIndex]?.[cardIndex];
                if (!layout) {
                  return null;
                }
                const image = layout.image[lang];
                const alt = card.caption.join(' ');
                return (
                  <div key={card.caption.join('')} className={styles.card}>
                    {layout.href ? (
                      <a
                        href={layout.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.cardLink}
                      >
                        <img
                          className={styles.cardImage}
                          src={image}
                          alt={alt}
                        />
                      </a>
                    ) : (
                      <img className={styles.cardImage} src={image} alt={alt} />
                    )}
                    {!layout.captionHidden && (
                      <p className={styles.cardCaption}>
                        {card.caption.map((line, index) => (
                          <span key={line}>
                            {index > 0 && <br />}
                            {line}
                          </span>
                        ))}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      <SiteFooter bordered />
    </div>
  );
};
